/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Content,
  GoogleGenAI,
  LiveCallbacks,
  LiveClientToolResponse,
  LiveConnectConfig,
  LiveServerContent,
  LiveServerMessage,
  LiveServerToolCall,
  LiveServerToolCallCancellation,
  Part,
  Session,
} from "@google/genai";

import { EventEmitter } from "eventemitter3";
import { difference } from "lodash";
import { LiveClientOptions, StreamingLog } from "../types";
import { base64ToArrayBuffer } from "./utils";
import { logger } from "../utils/logger";

/**
 * Event types that can be emitted by the MultimodalLiveClient.
 * Each event corresponds to a specific message from GenAI or client state change.
 */
export interface LiveClientEventTypes {
  // Emitted when audio data is received
  audio: (data: ArrayBuffer) => void;
  // Emitted when the connection closes
  close: (event: CloseEvent) => void;
  // Emitted when content is received from the server
  content: (data: LiveServerContent) => void;
  // Emitted when an error occurs
  error: (error: ErrorEvent) => void;
  // Emitted when the server interrupts the current generation
  interrupted: () => void;
  // Emitted for logging events
  log: (log: StreamingLog) => void;
  // Emitted when the connection opens
  open: () => void;
  // Emitted when the initial setup is complete
  setupcomplete: () => void;
  // Emitted when a tool call is received
  toolcall: (toolCall: LiveServerToolCall) => void;
  // Emitted when a tool call is cancelled
  toolcallcancellation: (
    toolcallCancellation: LiveServerToolCallCancellation
  ) => void;
  // Emitted when the current turn is complete
  turncomplete: () => void;
  // Emitted when user speech transcription is received
  inputTranscription: (text: string) => void;
  // Emitted when AI speech transcription is received
  outputTranscription: (text: string) => void;
}

/**
 * A event-emitting class that manages the connection to the websocket and emits
 * events to the rest of the application.
 * Supports both API key and ephemeral token authentication.
 * If you dont want to use react you can still use this.
 */
export class GenAILiveClient extends EventEmitter<LiveClientEventTypes> {
  protected client: GoogleGenAI | null = null;

  private _status: "connected" | "disconnected" | "connecting" = "disconnected";
  public get status() {
    return this._status;
  }

  private _session: Session | null = null;
  public get session() {
    return this._session;
  }

  private _model: string | null = null;
  public get model() {
    return this._model;
  }

  protected config: LiveConnectConfig | null = null;

  // Custom WebSocket for ephemeral token connections
  private _customWebSocket: WebSocket | null = null;
  
  // Track if using ephemeral token mode
  private _isEphemeralToken: boolean = false;
  
  // Store the token for reconnection purposes
  private _token: string | null = null;

  public getConfig() {
    return { ...this.config };
  }
  
  public isUsingEphemeralToken(): boolean {
    return this._isEphemeralToken;
  }

  constructor(options: LiveClientOptions) {
    super();
    
    if (options.token) {
      // Ephemeral token mode
      console.log("🔑 [GEMINI] Initializing with ephemeral token (secure mode)");
      this._isEphemeralToken = true;
      this._token = options.token;
      // Create client with v1alpha for ephemeral tokens
      this.client = new GoogleGenAI({ 
        apiKey: options.token,
        httpOptions: { apiVersion: 'v1alpha' }
      });
    } else if (options.apiKey) {
      // Legacy API key mode
      console.log("🔑 [GEMINI] Initializing with API key (legacy mode)");
      this._isEphemeralToken = false;
      this.client = new GoogleGenAI({ apiKey: options.apiKey });
    } else {
      throw new Error("Either apiKey or token must be provided");
    }
    
    this.send = this.send.bind(this);
    this.onopen = this.onopen.bind(this);
    this.onerror = this.onerror.bind(this);
    this.onclose = this.onclose.bind(this);
    this.onmessage = this.onmessage.bind(this);
  }

  protected log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  /**
   * Connect to Gemini Live API
   * Automatically uses the appropriate method based on authentication type
   */
  async connect(model: string, config: LiveConnectConfig): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      logger.connection("Connection already in progress or connected, skipping");
      return false;
    }

    logger.connection("Initiating connection to Gemini Live API");
    logger.connection("Model:", model);
    logger.connection("Using ephemeral token:", this._isEphemeralToken);

    this._status = "connecting";
    this.config = config;
    this._model = model;

    if (this._isEphemeralToken && this._token) {
      // Use custom WebSocket for ephemeral tokens
      return this.connectWithEphemeralToken(this._token);
    } else {
      // Use SDK for regular API keys
      return this.connectWithSDK(model, config);
    }
  }

  /**
   * Connect using the standard SDK (for API key authentication)
   */
  private async connectWithSDK(model: string, config: LiveConnectConfig): Promise<boolean> {
    logger.connection("Connecting with SDK (API key mode)");
    
    const callbacks: LiveCallbacks = {
      onopen: this.onopen,
      onmessage: this.onmessage,
      onerror: this.onerror,
      onclose: this.onclose,
    };

    try {
      logger.connection("Attempting to establish connection...");
      this._session = await this.client!.live.connect({
        model,
        config,
        callbacks,
      });
      logger.connection("Successfully connected to Gemini Live API");
    } catch (e) {
      console.error("❌ [GEMINI] Error connecting to GenAI Live:", e);
      this._status = "disconnected";
      return false;
    }

    this._status = "connected";
    return true;
  }

  /**
   * Connect using custom WebSocket for ephemeral tokens
   * MUST use BidiGenerateContentConstrained endpoint for locked tokens
   */
  private async connectWithEphemeralToken(token: string): Promise<boolean> {
    console.log("🔑 [GEMINI] Connecting with ephemeral token via custom WebSocket");
    
    // Build WebSocket URL - MUST use BidiGenerateContentConstrained for locked tokens
    const url = `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContentConstrained?access_token=${token}`;
    
    return new Promise((resolve) => {
      try {
        const ws = new WebSocket(url);
        this._customWebSocket = ws;
        
        ws.onopen = () => {
          console.log("✅ [GEMINI] WebSocket connected with ephemeral token");
          
          // Send EMPTY setup - all config is locked in the token
          ws.send(JSON.stringify({ setup: {} }));
          console.log("📤 [GEMINI] Sent empty setup message (config locked in token)");
          
          this._status = "connected";
          this.onopen();
          resolve(true);
        };
        
        ws.onmessage = async (event: MessageEvent) => {
          try {
            // Handle both text and Blob messages
            let messageText: string;
            if (event.data instanceof Blob) {
              messageText = await event.data.text();
            } else {
              messageText = event.data;
            }
            
            const data = JSON.parse(messageText);
            this.handleWebSocketMessage(data);
          } catch (e) {
            console.error("❌ [GEMINI] Error parsing WebSocket message:", e);
          }
        };
        
        ws.onerror = (event: Event) => {
          console.error("❌ [GEMINI] WebSocket error with ephemeral token:", event);
          this._status = "disconnected";
          this.emit("error", event as ErrorEvent);
          resolve(false);
        };
        
        ws.onclose = (event: CloseEvent) => {
          console.log(`🔒 [GEMINI] WebSocket closed. Code: ${event.code}, Reason: ${event.reason || 'No reason'}`);
          this._status = "disconnected";
          this._customWebSocket = null;
          this.onclose(event);
        };
        
      } catch (error) {
        console.error("❌ [GEMINI] Failed to create WebSocket:", error);
        this._status = "disconnected";
        resolve(false);
      }
    });
  }

  /**
   * Handle incoming WebSocket messages for ephemeral token connections
   */
  private handleWebSocketMessage(data: any) {
    // Convert to LiveServerMessage format and use existing handler
    // Use type assertion since we're only populating the fields we need
    const message = {
      setupComplete: data.setupComplete,
      serverContent: data.serverContent,
      toolCall: data.toolCall,
      toolCallCancellation: data.toolCallCancellation,
    } as LiveServerMessage;
    
    this.onmessage(message);
  }

  public disconnect() {
    // Handle custom WebSocket disconnection
    if (this._customWebSocket) {
      logger.connection("Disconnecting custom WebSocket");
      if (this._customWebSocket.readyState === WebSocket.OPEN) {
        this._customWebSocket.close();
      }
      this._customWebSocket = null;
      this._status = "disconnected";
      logger.connection("Successfully disconnected custom WebSocket");
      return true;
    }
    
    // Handle SDK session disconnection
    if (!this._session) {
      logger.connection("No active session to disconnect");
      return false;
    }
    logger.connection("Disconnecting from Gemini Live API");
    this._session?.close();
    this._session = null;
    this._status = "disconnected";

    logger.connection("Successfully disconnected from Gemini Live API");
    return true;
  }

  protected onopen() {
    logger.connection("WebSocket connection opened with Gemini Live API");
    this.emit("open");
  }

  protected onerror(e: ErrorEvent) {
    console.error("❌ [GEMINI] WebSocket error from Gemini Live API:", e.message);
    this.emit("error", e);
  }

  protected onclose(e: CloseEvent) {
    console.log(`🔌 [GEMINI] WebSocket connection closed with Gemini Live API. Code: ${e.code}, Reason: ${e.reason || 'No reason provided'}, WasClean: ${e.wasClean}`);
    logger.connection("WebSocket connection closed with Gemini Live API", e.reason ? `Reason: ${e.reason}` : "");
    this.emit("close", e);
  }

  protected async onmessage(message: LiveServerMessage) {
    logger.websocket("Received message from Gemini Live API:", message);
    
    if (message.setupComplete) {
      logger.connection("Setup completed with Gemini Live API");
      this.emit("setupcomplete");
      return;
    }
    if (message.toolCall) {
      logger.tool("Tool call received from Gemini:", message.toolCall);
      this.emit("toolcall", message.toolCall);
      return;
    }
    if (message.toolCallCancellation) {
      logger.tool("Tool call cancellation received from Gemini:", message.toolCallCancellation);
      this.emit("toolcallcancellation", message.toolCallCancellation);
      return;
    }

    if (message.serverContent) {
      const { serverContent } = message;
      logger.websocket("Server content received from Gemini:", serverContent);
      
      if ("interrupted" in serverContent) {
        logger.websocket("Generation interrupted by Gemini");
        this.emit("interrupted");
        return;
      }
      if ("turnComplete" in serverContent) {
        logger.websocket("Turn completed by Gemini");
        this.emit("turncomplete");
      }

      // Handle input transcription (user speech)
      if ("inputTranscription" in serverContent && serverContent.inputTranscription) {
        const transcriptionText = (serverContent.inputTranscription as any).text;
        logger.websocket("User speech transcription:", transcriptionText);
        this.emit("inputTranscription", transcriptionText);
      }

      // Handle output transcription (AI speech)
      if ("outputTranscription" in serverContent && serverContent.outputTranscription) {
        const transcriptionText = (serverContent.outputTranscription as any).text;
        logger.websocket("AI speech transcription:", transcriptionText);
        this.emit("outputTranscription", transcriptionText);
      }

      if ("modelTurn" in serverContent) {
        logger.websocket("Model turn received from Gemini");
        let parts: Part[] = serverContent.modelTurn?.parts || [];

        // DEBUG: Log what the AI transcribed/understood
        const textParts = parts.filter(p => p.text).map(p => p.text);
        if (textParts.length > 0) {
          logger.tool("🎯 [TRANSCRIPTION] AI understood user speech as:", textParts.join(" "));
        }

        // when its audio that is returned for modelTurn
        const audioParts = parts.filter(
          (p) => p.inlineData && p.inlineData.mimeType?.startsWith("audio/pcm")
        );
        const base64s = audioParts.map((p) => p.inlineData?.data);

        logger.audio(`Processing ${audioParts.length} audio parts from Gemini`);

        // strip the audio parts out of the modelTurn
        const otherParts = difference(parts, audioParts);

        base64s.forEach((b64) => {
          if (b64) {
            const data = base64ToArrayBuffer(b64);
            this.emit("audio", data);
          }
        });
        if (!otherParts.length) {
          return;
        }

        parts = otherParts;

        const content: { modelTurn: Content } = { modelTurn: { parts } };
        logger.websocket("Emitting content from Gemini:", content);
        this.emit("content", content);
      }
    } else {
      logger.websocket("Received unmatched message from Gemini:", message);
    }
  }

  /**
   * send realtimeInput, this is base64 chunks of "audio/pcm" and/or "image/jpg"
   */
  sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>) {
    // Check if using custom WebSocket
    if (this._customWebSocket) {
      if (this._customWebSocket.readyState !== WebSocket.OPEN) {
        logger.warn(`Cannot send realtime input - WebSocket not open`);
        return;
      }
      
      logger.websocket(`Sending realtime input via custom WebSocket: ${chunks.length} chunks`);
      
      try {
        for (const ch of chunks) {
          this._customWebSocket.send(JSON.stringify({
            realtime_input: { media_chunks: [ch] }
          }));
        }
      } catch (error) {
        logger.error(`Error sending realtime input via custom WebSocket:`, error);
        this._status = "disconnected";
        this.emit("close", { code: 1006, reason: "Send failed", wasClean: false } as CloseEvent);
      }
      return;
    }
    
    // Use SDK session
    if (!this._session || this._status !== "connected") {
      logger.warn(`Cannot send realtime input - Session status: ${this._status}`);
      return;
    }

    logger.websocket(`Sending realtime input to Gemini: ${chunks.length} chunks`);
    
    let hasAudio = false;
    let hasVideo = false;
    
    try {
      for (const ch of chunks) {
        if (this._status !== "connected") {
          logger.warn(`Connection lost during chunk send, stopping`);
          break;
        }
        
        this._session?.sendRealtimeInput({ media: ch });
        
        if (ch.mimeType.includes("audio")) {
          hasAudio = true;
        }
        if (ch.mimeType.includes("image")) {
          hasVideo = true;
        }
        if (hasAudio && hasVideo) {
          break;
        }
      }
      
      const message =
        hasAudio && hasVideo
          ? "audio + video"
          : hasAudio
          ? "audio"
          : hasVideo
          ? "video"
          : "unknown";
      logger.websocket(`Realtime input type: ${message}`);
    } catch (error) {
      logger.error(`Error sending realtime input:`, error);
      if (this._status === "connected") {
        this._status = "disconnected";
        this.emit("close", { code: 1006, reason: "Send failed", wasClean: false } as CloseEvent);
      }
    }
  }

  /**
   *  send a response to a function call and provide the id of the functions you are responding to
   */
  sendToolResponse(toolResponse: LiveClientToolResponse) {
    // Check if using custom WebSocket
    if (this._customWebSocket) {
      if (this._customWebSocket.readyState !== WebSocket.OPEN) {
        console.warn(`⚠️ [GEMINI] Cannot send tool response - WebSocket not open`);
        return;
      }
      
      if (toolResponse.functionResponses && toolResponse.functionResponses.length) {
        try {
          logger.tool(`Sending tool response via custom WebSocket:`, toolResponse);
          this._customWebSocket.send(JSON.stringify({
            tool_response: { function_responses: toolResponse.functionResponses }
          }));
        } catch (error) {
          console.error(`❌ [GEMINI] Error sending tool response via custom WebSocket:`, error);
        }
      }
      return;
    }
    
    // Use SDK session
    if (!this._session || this._status !== "connected") {
      console.warn(`⚠️ [GEMINI] Cannot send tool response - Session status: ${this._status}`);
      return;
    }
    
    if (
      toolResponse.functionResponses &&
      toolResponse.functionResponses.length
    ) {
      try {
        logger.tool(`Sending tool response to Gemini:`, toolResponse);
        this._session?.sendToolResponse({
          functionResponses: toolResponse.functionResponses,
        });
      } catch (error) {
        console.error(`❌ [GEMINI] Error sending tool response:`, error);
      }
    }
  }

  /**
   * send normal content parts such as { text }
   */
  send(parts: Part | Part[], turnComplete: boolean = true) {
    // Check if using custom WebSocket
    if (this._customWebSocket) {
      if (this._customWebSocket.readyState !== WebSocket.OPEN) {
        console.warn(`⚠️ [GEMINI] Cannot send content - WebSocket not open`);
        return;
      }
      
      try {
        const partsArray = Array.isArray(parts) ? parts : [parts];
        logger.websocket(`Sending content via custom WebSocket:`, { parts: partsArray, turnComplete });
        
        this._customWebSocket.send(JSON.stringify({
          client_content: {
            turns: [{ role: 'user', parts: partsArray }],
            turn_complete: turnComplete
          }
        }));
      } catch (error) {
        console.error(`❌ [GEMINI] Error sending content via custom WebSocket:`, error);
      }
      return;
    }
    
    // Use SDK session
    if (!this._session || this._status !== "connected") {
      console.warn(`⚠️ [GEMINI] Cannot send content - Session status: ${this._status}`);
      return;
    }

    try {
      logger.websocket(`Sending content to Gemini:`, {
        parts: Array.isArray(parts) ? parts : [parts],
        turnComplete
      });
      this._session?.sendClientContent({ turns: parts, turnComplete });
    } catch (error) {
      console.error(`❌ [GEMINI] Error sending content:`, error);
    }
  }
}
